import { timeRecordsListingConfiguration } from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrieveTimeRecordsListingConfigurationRequest(
  state: State,
  planzone: Boundaries["retrieveTimeRecordsListingConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToPending(),
          configuration: timeRecordsListingConfiguration,
        },
      ];
}

function handleRetrieveTimeRecordsListingConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["retrieveTimeRecordsListingConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleRetrieveTimeRecordsListingConfigurationFailure(
  state: State,
  planzone: Boundaries["retrieveTimeRecordsListingConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToFailure(),
          configuration: timeRecordsListingConfiguration,
        },
      ];
}

function handleEditTimeRecordsListingConfigurationRequest(
  state: State,
  {
    planzone,
    nextConfiguration,
  }: Boundaries["editTimeRecordsListingConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration: nextConfiguration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration: nextConfiguration,
        },
      ];
}

function handleEditTimeRecordsListingConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editTimeRecordsListingConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleEditTimeRecordsListingConfigurationFailure(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editTimeRecordsListingConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveTimeRecordsListingConfigurationRequested]:
    handleRetrieveTimeRecordsListingConfigurationRequest,
  [events.retrieveTimeRecordsListingConfigurationSucceeded]:
    handleRetrieveTimeRecordsListingConfigurationSuccess,
  [events.retrieveTimeRecordsListingConfigurationFailed]:
    handleRetrieveTimeRecordsListingConfigurationFailure,
  [events.editTimeRecordsListingConfigurationRequested]:
    handleEditTimeRecordsListingConfigurationRequest,
  [events.editTimeRecordsListingConfigurationSucceeded]:
    handleEditTimeRecordsListingConfigurationSuccess,
  [events.editTimeRecordsListingConfigurationFailed]:
    handleEditTimeRecordsListingConfigurationFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
